/**
 * Pure, React-free helpers for the Estimate domain: id generation, output
 * formatting for display, and small projections. The estimation MATH lives in
 * {@link ./engine}; this file is presentation/plumbing glue only.
 */

import { v4 as uuid } from 'uuid';
import type { EstimateOutputValue, EstimateOutputs } from './types';

/** Deterministic document id for a new estimate. */
export function estimateDocId(): string {
  return `est_${uuid()}`;
}

/** Formats a single computed output value for display. */
export function formatOutputValue(value: EstimateOutputValue): string {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '—';
    // Trim to at most 4 decimals, dropping trailing zeros.
    return Number(value.toFixed(4)).toLocaleString(undefined, {
      maximumFractionDigits: 4,
    });
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return value;
}

/** Returns [key, value] output pairs in a stable, display-friendly order. */
export function outputEntries(
  outputs: EstimateOutputs,
): Array<[string, EstimateOutputValue]> {
  return Object.entries(outputs);
}

/** Picks a headline output (the first one) for compact list rendering. */
export function headlineOutput(
  outputs: EstimateOutputs,
): { key: string; value: EstimateOutputValue } | null {
  const entries = Object.entries(outputs);
  if (entries.length === 0) return null;
  const [key, value] = entries[0];
  return { key, value };
}

/** Safely parses a JSON-encoded outputs blob from a pointer-row attribute. */
export function parseOutputsAttr(raw: string | undefined): EstimateOutputs {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as EstimateOutputs;
    }
    return {};
  } catch {
    return {};
  }
}
